import { Point } from "../ImageWithBoundingBox";
import { orderCorners } from "../utils/obbGeometry";

/**
 * Get bounding box from points
 */
export const getBoundingBox = (points: Point[]) => {
  if (!points.length) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
};

/**
 * Build a canvas path through the box corners (in canvas coordinates), so
 * oriented boxes draw as their true rotated quad instead of an AABB.
 * Corners are ring-ordered first, since detections arrive row-major.
 */
export const getQuadPath = (
  points: Point[],
  imgRect: { width: number; height: number; offsetX: number; offsetY: number },
): Path2D => {
  const path = new Path2D();
  const corners = orderCorners(points);
  corners.forEach(([x, y], i) => {
    const px = imgRect.offsetX + x * imgRect.width;
    const py = imgRect.offsetY + y * imgRect.height;
    if (i === 0) path.moveTo(px, py);
    else path.lineTo(px, py);
  });
  path.closePath();
  return path;
};

/**
 * Check if point is inside a box using point-in-polygon test
 */
export const isPointInBox = (
  point: Point,
  box: { points: Point[] },
): boolean => {
  let inside = false;
  for (let i = 0, j = box.points.length - 1; i < box.points.length; j = i++) {
    const xi = box.points[i][0];
    const yi = box.points[i][1];
    const xj = box.points[j][0];
    const yj = box.points[j][1];

    const intersect =
      yi > point[1] !== yj > point[1] &&
      point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};
